import React, { FC, useState } from "react";
import Box from "../../Unknown/Box";
import DataGrid, {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortItem,
} from "../../Unknown/DataGrid";
import useDashboardColumns, {
  DashboardColumnsParams,
} from "./useDashboardColumns";
import { AppointmentRowItem } from "./types";
import useStyles from "./useStyles";
import AppointmentTableTopBar from "../AppointmentTableTopBar";
import { DateRangePickerProps } from "../../Unknown/DateRangePicker";
import { PAGE_SIZE_OPTIONS } from "../../../common/constants";
import CustomerLogInDialog, {
  CustomerLogInDialogProps,
} from "../../Customer/CustomerLogInDialog";
import { AppointmentMarkNoShowDialogProps } from "../AppointmentMarkNoShowDialog";
import AppointmentCheckInDialog from "../AppointmentCheckInDialog";

type AppointmentsTableGridProps = {
  columnsConfig: DashboardColumnsParams;
  rows: AppointmentRowItem[];
  isLoading?: boolean;
  isFetching?: boolean;
  dateRangePickerProps?: DateRangePickerProps<Date>;
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  loginDialogProps: CustomerLogInDialogProps | null;
  checkInDialogProps: AppointmentMarkNoShowDialogProps;
  filterModel: GridFilterModel;
  sortModel: GridSortItem[];
  paginationModel: { page: number; pageSize: number };
  onFilterModelChange: (model: GridFilterModel) => void;
  onSortModelChange: (model: GridSortItem[]) => void;
  onPaginationModelChange: (model: { page: number; pageSize: number }) => void;
  rowCount: number;
  fetchAllRowsForExport?: () => Promise<AppointmentRowItem[]>;
};

const AppointmentsTableGrid: FC<AppointmentsTableGridProps> = ({
  columnsConfig,
  rows,
  isLoading,
  isFetching,
  dateRangePickerProps,
  initialColumnVisibilityModel,
  loginDialogProps,
  checkInDialogProps,
  filterModel,
  sortModel,
  paginationModel,
  onFilterModelChange,
  onSortModelChange,
  onPaginationModelChange,
  rowCount,
  fetchAllRowsForExport,
}) => {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    initialColumnVisibilityModel,
  );

  const { root, panel } = useStyles();

  const columns = useDashboardColumns(columnsConfig);

  return (
    <>
      <Box sx={{ position: "relative" }}>
        {isFetching && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          />
        )}
        <DataGrid
          autoHeight
          getRowHeight={() => "auto"}
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          loading={isLoading}
          className={root}
          pagination
          filterMode="server"
          sortingMode="server"
          paginationMode="server"
          rowCount={rowCount}
          onFilterModelChange={onFilterModelChange}
          filterModel={filterModel}
          onSortModelChange={onSortModelChange}
          sortModel={sortModel}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          getRowClassName={(params) => {
            const { isPast } = params.row;

            return isPast ? "row--past" : "row--general";
          }}
          slots={{ toolbar: AppointmentTableTopBar }}
          slotProps={{
            toolbar: {
              dateRangePickerProps,
              fetchAllRowsForExport,
            },
            panel: {
              className: panel,
            },
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
        />
      </Box>
      {loginDialogProps && <CustomerLogInDialog {...loginDialogProps} />}
      {checkInDialogProps && (
        <AppointmentCheckInDialog {...checkInDialogProps} />
      )}
    </>
  );
};

export default AppointmentsTableGrid;
